import React, { useCallback, useEffect, useState } from 'react';
import { Camera, Loader2, Sparkles, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { adminVehiclesAPI, uploadsAPI } from '../services/api';
import { toast } from 'sonner';

// Admin › Véhicules : AI-generated pictures for models without one (queue, daily cap, auto on selection)
const VehicleImageAiCard = ({ onChanged }) => {
  const [st, setSt] = useState(null);
  const [limit, setLimit] = useState(20);
  const [form, setForm] = useState({ auto_on_select: true, daily_cap: 60 });
  const load = useCallback(() => adminVehiclesAPI.aiImagesStatus().then(s => { setSt(s); setForm(s.settings); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const busy = !!(st?.running || st?.queued);
  useEffect(() => {
    if (!busy) return undefined;
    const id = setInterval(async () => {
      try { const s = await adminVehiclesAPI.aiImagesStatus(); setSt(s); if (!s.running && !s.queued) { clearInterval(id); onChanged?.(); } } catch { /* retry */ }
    }, 4000);
    return () => clearInterval(id);
  }, [busy, onChanged]);

  const generate = async () => {
    try { const r = await adminVehiclesAPI.aiImagesGenerate({ limit }); toast.success(`${r.queued} photo(s) mise(s) en file de génération`); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Lancement impossible'); }
  };
  const save = async () => {
    try { const s = await adminVehiclesAPI.aiImagesSettings(form); setForm(s); toast.success('Réglages enregistrés'); } catch { toast.error('Enregistrement impossible'); }
  };
  const clear = async () => { try { const r = await adminVehiclesAPI.aiImagesClearQueue(); toast.success(`${r.removed} demande(s) retirée(s)`); load(); } catch { toast.error('Impossible'); } };

  if (!st) return null;
  return (
    <div className="bg-white rounded-lg shadow-sm p-4 mb-6" data-testid="vehicle-image-ai-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Camera size={16} className="text-orange-500" /> Photos de modèles (IA)</h3>
          <p className="text-xs text-gray-500" data-testid="vehicle-image-ai-summary">
            {st.with_image}/{st.total} modèles avec photo · {st.missing} sans photo · {st.ai_generated} générée(s) par IA · aujourd'hui {st.today}/{st.settings.daily_cap || '∞'}
            {busy && <span className="ml-2 text-orange-600 font-medium" data-testid="vehicle-image-ai-progress">· {st.queued} en file{st.running ? ', génération en cours…' : ''}</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={limit} onChange={e => setLimit(Number(e.target.value))} data-testid="vehicle-image-ai-limit" className="text-sm border border-gray-200 rounded-md py-1.5 px-2 bg-white">
            {[5, 10, 20, 50, 100].map(n => <option key={n} value={n}>{n} modèles</option>)}
          </select>
          <Button size="sm" onClick={generate} disabled={!st.ai_ready || !st.missing} data-testid="vehicle-image-ai-generate" className="bg-orange-500 hover:bg-orange-600 text-white" title="Les modèles les plus sélectionnés et ceux ayant des pièces compatibles passent en premier">
            {st.running ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Sparkles size={14} className="mr-1" />} Générer les photos manquantes
          </Button>
          {!!(st.queued || st.failed) && <button type="button" onClick={clear} className="text-xs text-gray-500 hover:text-red-600 inline-flex items-center gap-1" data-testid="vehicle-image-ai-clear"><Trash2 size={12} /> Vider la file ({st.queued + st.failed})</button>}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-3 text-sm" data-testid="vehicle-image-ai-settings">
        <label className="inline-flex items-center gap-2 text-gray-700"><input type="checkbox" checked={!!form.auto_on_select} onChange={e => setForm({ ...form, auto_on_select: e.target.checked })} data-testid="vehicle-image-ai-auto" /> Générer automatiquement quand un visiteur sélectionne un modèle sans photo</label>
        <label className="text-xs text-gray-600">Plafond / jour<br /><input type="number" min={0} max={2000} value={form.daily_cap} onChange={e => setForm({ ...form, daily_cap: Number(e.target.value) })} data-testid="vehicle-image-ai-cap" className="mt-0.5 w-24 h-8 text-sm border border-gray-300 rounded-md px-2" /></label>
        <Button size="sm" variant="outline" onClick={save} data-testid="vehicle-image-ai-save">Enregistrer</Button>
        <span className="text-[11px] text-gray-400">Chaque matin, 10 modèles populaires sans photo sont aussi générés automatiquement.</span>
      </div>
      {!!st.recent?.length && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="vehicle-image-ai-recent">
          {st.recent.slice(0, 16).map(r => (
            <div key={`${r.brand}|${r.model}`} className="flex items-center gap-2 border border-gray-100 rounded-md px-2 py-1 text-xs" data-testid={`vehicle-image-ai-recent-${r.status}`} title={r.error || r.status}>
              {r.image ? <img src={uploadsAPI.resolveUrl(r.image)} alt="" className="h-8 w-12 object-contain" /> : r.status === 'running' ? <Loader2 size={14} className="animate-spin text-orange-500" /> : <span className={`w-2 h-2 rounded-full ${r.status === 'failed' ? 'bg-red-500' : 'bg-gray-300'}`} />}
              <span className="text-gray-700 truncate max-w-[180px]">{r.brand} {r.model}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VehicleImageAiCard;
